/**
 * Checkout flow orchestrator hook
 *
 * Two-phase checkout:
 * Phase 1 (createOrder): Auth -> Sync cart -> Validate -> Create order -> returns clientSecret
 * Phase 2 (finalizeOrder): After Stripe payment confirms -> clear cart, update Redux
 */

import { useState, useCallback, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectCartItems, clearCart } from '@/store/cartSlice'
import {
  setOrderId,
  setOrderNumber,
  setTrackingCode,
  setEstimatedTime,
  setOrderStatus,
  setError,
} from '@/store/orderSlice'
import { menuWidgetApi } from '@/store/convenienceAccessor'
import { getEnvironmentConfig } from '@/config/environment'
import {
  IDEMPOTENCY_SESSION_KEY,
  safeSessionGet,
  safeSessionSet,
  pickupTimeToISO,
  extractErrorMessage,
  ensureAuth,
  type CheckoutStep,
  type CheckoutResult,
  type CheckoutOptions,
} from '@/hooks/checkoutUtils'

export type { CheckoutStep, CheckoutResult, CheckoutOptions }

interface UseCheckoutFlowReturn {
  step: CheckoutStep
  error: string | null
  orderResult: CheckoutResult | null
  createOrder: (opts: CheckoutOptions) => Promise<CheckoutResult | null>
  finalizeOrder: () => void
  reset: () => void
}

export function useCheckoutFlow(): UseCheckoutFlowReturn {
  const dispatch = useAppDispatch()
  const items = useAppSelector(selectCartItems)
  const config = getEnvironmentConfig()

  const [step, setStep] = useState<CheckoutStep>('idle')
  const [error, setLocalError] = useState<string | null>(null)
  const [orderResult, setOrderResult] = useState<CheckoutResult | null>(null)
  const orderResultRef = useRef<CheckoutResult | null>(null)
  const isProcessingRef = useRef(false)
  // Stable idempotency key per checkout session — reused on retries
  const idempotencyKeyRef = useRef<string | null>(safeSessionGet(IDEMPOTENCY_SESSION_KEY))

  const [addCartItem] = menuWidgetApi.cartItem.add()
  const [deleteCart] = menuWidgetApi.cart.delete()
  const [addCheckoutValidate] = menuWidgetApi.checkoutValidate.add()
  const [addCheckout] = menuWidgetApi.checkout.add()
  const [addConfirmPayment] = menuWidgetApi.confirmPayment.add()

  /**
   * Phase 1: Create order on backend and get Stripe client_secret
   */
  const createOrder = useCallback(async (opts: CheckoutOptions): Promise<CheckoutResult | null> => {
    if (isProcessingRef.current) return null
    isProcessingRef.current = true

    setLocalError(null)
    dispatch(setError(null))

    try {
      // Step 1: Authenticate
      setStep('authenticating')
      await ensureAuth()

      // Step 2: Sync local cart to backend
      setStep('syncing-cart')

      await deleteCart({ restaurant_id: config.restaurantId }).unwrap().catch(() => {
        // Expected if no cart exists yet
      })

      for (let i = 0; i < items.length; i++) {
        const item = items[i]

        // Build customizations from selectedModifiers (regular row ingredient selections)
        const customizations: Array<Record<string, unknown>> = item.selectedModifiers.map(mod => ({
          type: 'row_ingredient',
          row_id: mod.groupId,
          row_ingredient_id: mod.optionId,
          row_title: mod.groupName,
          ingredient_name: mod.optionName,
          quantity: mod.quantity ?? 1,
        }))

        // Add combo group selections (side, drink, etc.)
        if (item.comboSelections?.groupSelections) {
          for (const sel of item.comboSelections.groupSelections) {
            customizations.push({
              type: 'combo_option',
              combo_option_id: sel.optionId,
              row_title: sel.groupName,
              ingredient_name: sel.optionName,
              quantity: 1,
            })
          }
        }

        // Add combo main meal customizations (builder rows within combo)
        if (item.comboSelections?.mainMealCustomizations) {
          for (const mod of item.comboSelections.mainMealCustomizations) {
            customizations.push({
              type: 'row_ingredient',
              row_id: mod.groupId,
              row_ingredient_id: mod.optionId,
              row_title: mod.groupName,
              ingredient_name: mod.optionName,
              quantity: mod.quantity ?? 1,
            })
          }
        }

        const specialInstructions = (item.specialInstructions ?? '').trim()

        await addCartItem({
          restaurant_id: config.restaurantId,
          menu_item_id: item.menuItem.id,
          quantity: item.quantity,
          customizations,
          ...(specialInstructions ? { special_instructions: specialInstructions } : {}),
        }).unwrap()
      }

      // Step 3: Validate
      setStep('validating')

      const scheduledPickupISO = pickupTimeToISO(opts.scheduledPickupAt)

      const safeName = (opts.customerName || 'Guest').slice(0, 100)
      const safeEmail = (opts.customerEmail || '').slice(0, 254)
      const safePhone = (opts.customerPhone || '').slice(0, 20)

      const validatePayload = {
        restaurant_id: config.restaurantId,
        customer_info: {
          name: safeName,
          email: safeEmail,
          phone: safePhone,
          notification_channel: opts.notificationChannel || '',
          contact_verified: opts.contactVerified || false,
        },
        ...(scheduledPickupISO ? { scheduled_pickup_at: scheduledPickupISO } : {}),
      }

      const validation = await addCheckoutValidate(validatePayload).unwrap()

      if (validation && !validation.is_valid) {
        const errors = validation.errors || []
        const errorMsg = errors.map((e: string | { message: string }) =>
          typeof e === 'string' ? e : e.message
        ).join(', ') || 'Validation failed'
        throw new Error(errorMsg)
      }

      // Step 4: Create order (gets client_secret from Stripe)
      setStep('processing')

      // Generate a stable idempotency key for this checkout attempt so that
      // network retries do not create duplicate Stripe PaymentIntents.
      // Reuse the same idempotency key for retries within the same checkout session
      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current = opts.idempotency_key || crypto.randomUUID()
      }
      const idempotencyKey = idempotencyKeyRef.current
      // Persist to sessionStorage so remounts after 3DS redirect reuse the same key
      safeSessionSet(IDEMPOTENCY_SESSION_KEY, idempotencyKey)

      const checkoutPayload = {
        restaurant_id: config.restaurantId,
        customer_info: {
          name: safeName,
          email: safeEmail,
          phone: safePhone,
          notification_channel: opts.notificationChannel || '',
          contact_verified: opts.contactVerified || false,
        },
        ...(scheduledPickupISO ? { scheduled_pickup_at: scheduledPickupISO } : {}),
        tip_amount: Math.max(0, opts.tipAmount || 0),
        order_type: opts.orderType || 'pickup',
        menu_id: opts.menuId || null,
        idempotency_key: idempotencyKey,
        notes: (opts.notes || '').slice(0, 1000),
        ...(opts.locationId ? { location_id: opts.locationId } : {}),
        ...(opts.orderType === 'delivery' && opts.deliveryInfo ? {
          delivery_address: {
            street: (opts.deliveryInfo.street || '').slice(0, 200),
            city: (opts.deliveryInfo.city || '').slice(0, 100),
            state: (opts.deliveryInfo.state || '').slice(0, 50),
            zip_code: (opts.deliveryInfo.zipCode || '').slice(0, 10),
            apartment_number: (opts.deliveryInfo.apartmentNumber || '').slice(0, 20),
            delivery_instructions: (opts.deliveryInfo.deliveryInstructions || '').slice(0, 500),
          }
        } : {}),
      }

      const result = await addCheckout(checkoutPayload).unwrap()

      const checkoutResult: CheckoutResult = {
        orderId: result.order_id,
        orderNumber: result.order_number,
        trackingCode: result.tracking_code,
        total: result.total,
        currency: result.currency,
        clientSecret: result.client_secret,
        status: result.status,
        estimatedReadyAt: result.estimated_ready_at || null,
      }

      setOrderResult(checkoutResult)
      orderResultRef.current = checkoutResult
      dispatch(setOrderId(checkoutResult.orderId))
      dispatch(setOrderNumber(checkoutResult.orderNumber))
      dispatch(setTrackingCode(checkoutResult.trackingCode))

      // Don't finalize yet — wait for Stripe payment
      setStep('awaiting-payment')
      return checkoutResult

    } catch (err: unknown) {
      console.error('[CHECKOUT] createOrder failed:', err)

      const message = extractErrorMessage(err)

      // Idempotency key collision — clear it so next attempt gets a fresh key
      if (message.toLowerCase().includes('idempotent')) {
        idempotencyKeyRef.current = null
        safeSessionSet(IDEMPOTENCY_SESSION_KEY, null)
      }

      setLocalError(message)
      dispatch(setError(message))
      setStep('error')
      isProcessingRef.current = false
      return null
    }
  }, [items, config.restaurantId, dispatch, deleteCart, addCartItem, addCheckoutValidate, addCheckout])

  /**
   * Phase 2: Finalize order after successful payment.
   * Calls backend confirm-payment to mark order as paid and trigger notifications.
   *
   * Uses orderResultRef (not state) to avoid stale closure in StripePaymentForm's
   * handleSubmit — by the time onSuccess fires, the state-based orderResult may
   * still be null in the captured closure.
   */
  const finalizeOrder = useCallback(async () => {
    const result = orderResultRef.current
    if (!result) return

    // Tell the backend payment succeeded — this marks as paid and triggers notifications
    try {
      await addConfirmPayment({
        id: result.orderId,
        restaurant_id: config.restaurantId,
      }).unwrap()
    } catch (err) {
      // Log but don't block — payment already succeeded on Stripe's side
      console.warn('[CHECKOUT] confirm-payment call failed:', err)
    }

    dispatch(setOrderStatus('confirmed'))
    dispatch(clearCart())

    const eta = result.estimatedReadyAt || new Date(Date.now() + 30 * 60 * 1000).toISOString()
    dispatch(setEstimatedTime(eta))

    isProcessingRef.current = false
    idempotencyKeyRef.current = null  // Next checkout session gets a fresh key
    safeSessionSet(IDEMPOTENCY_SESSION_KEY, null)
    setStep('done')
  }, [config.restaurantId, dispatch, addConfirmPayment])

  const reset = useCallback(() => {
    setStep('idle')
    setLocalError(null)
    setOrderResult(null)
    orderResultRef.current = null
    isProcessingRef.current = false
    idempotencyKeyRef.current = null  // New checkout attempt gets a fresh key
    safeSessionSet(IDEMPOTENCY_SESSION_KEY, null)
    dispatch(setError(null))
  }, [dispatch])

  return { step, error, orderResult, createOrder, finalizeOrder, reset }
}
